/**
 * AW Swiper Component – Init + guards + multi-instance
 * Option A: Use existing window.Swiper (warn if missing).
 * Option B: Lazy-load Swiper from CDN if not present, then init.
 * Shopify: runs on DOMContentLoaded + shopify:section:load / reorder / select.
 */

(function () {
  'use strict';

  var ROOT_SELECTOR = '.aw-swiper';
  var DATA_INITIALIZED = 'data-aw-swiper-initialized';
  var SWIPER_CDN_JS = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
  var SWIPER_CDN_CSS = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';

  function ensureSwiperLoaded(opts) {
    var mode = (opts && opts.loadSwiperFromCDN) ? 'cdn' : 'existing';
    if (typeof window.Swiper === 'function') return Promise.resolve();
    if (mode === 'existing') {
      console.warn('[aw-swiper] window.Swiper is missing. Load swiper-bundle.min.js or set loadSwiperFromCDN: true.');
      return Promise.reject(new Error('Swiper not loaded'));
    }
    return new Promise(function (resolve, reject) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = SWIPER_CDN_CSS;
      document.head.appendChild(link);
      var script = document.createElement('script');
      script.src = SWIPER_CDN_JS;
      script.onload = function () { resolve(); };
      script.onerror = function () { reject(new Error('Swiper CDN load failed')); };
      document.head.appendChild(script);
    });
  }

  function ensureGSAPLoaded() {
    return typeof window.gsap !== 'undefined';
  }

  function setWrapperId(root) {
    var wrapper = root.querySelector('.aw-swiper__wrapper');
    var id = root.getAttribute('data-aw-swiper-id');
    if (!wrapper || !id) return;
    var safeId = id.replace(/[^a-zA-Z0-9_-]/g, '_');
    wrapper.id = 'aw-swiper-wrapper-' + safeId;
    var prevBtns = root.querySelectorAll('.aw-swiper__btn-prev, .aw-swiper__btn-prev-mobile');
    var nextBtns = root.querySelectorAll('.aw-swiper__btn-next, .aw-swiper__btn-next-mobile');
    prevBtns.forEach(function (el) { el.setAttribute('aria-controls', wrapper.id); });
    nextBtns.forEach(function (el) { el.setAttribute('aria-controls', wrapper.id); });
  }

  /* Apply scale/opacity. When slide becomes active, keep it small then animate to full size (called at transitionEnd). */
  function updateSlideStyles(root) {
    var swiperEl = root.querySelector('.aw-swiper__swiper');
    if (!swiperEl) return;
    var active = swiperEl.querySelector('.swiper-slide.swiper-slide-active');
    var next = swiperEl.querySelector('.swiper-slide.swiper-slide-next');
    var prev = swiperEl.querySelector('.swiper-slide.swiper-slide-prev');
    if (typeof window.gsap !== 'undefined') {
      if (active) window.gsap.to(active, { scale: 1, opacity: 1, duration: 0.6, ease: 'power2.out' });
      if (next) window.gsap.to(next, { scale: 0.95, opacity: 0.5, duration: 0.6, ease: 'power2.out' });
      if (prev) window.gsap.to(prev, { scale: 1, opacity: 0.5, duration: 0.6, ease: 'power2.out' });
    }
  }

  /* At transition start: keep new active at 0.95 so it stays small during the slide move. */
  function holdActiveSmall(root) {
    var swiperEl = root.querySelector('.aw-swiper__swiper');
    if (!swiperEl) return;
    var active = swiperEl.querySelector('.swiper-slide.swiper-slide-active');
    if (active && typeof window.gsap !== 'undefined') {
      window.gsap.set(active, { scale: 0.95, opacity: 1 });
    }
  }

  function initOne(root, opts) {
    if (root.getAttribute(DATA_INITIALIZED) === 'true') return;
    var swiperEl = root.querySelector('.aw-swiper__swiper');
    var wrapper = root.querySelector('.aw-swiper__wrapper');
    if (!swiperEl || !wrapper) return;

    var id = root.getAttribute('data-aw-swiper-id') || 'aw-swiper-' + Math.random().toString(36).slice(2, 11);
    if (!root.getAttribute('data-aw-swiper-id')) root.setAttribute('data-aw-swiper-id', id);
    setWrapperId(root);

    var prevDesktop = root.querySelector('.aw-swiper__btn-prev');
    var nextDesktop = root.querySelector('.aw-swiper__btn-next');
    var prevMobile = root.querySelector('.aw-swiper__btn-prev-mobile');
    var nextMobile = root.querySelector('.aw-swiper__btn-next-mobile');

    var swiper = new window.Swiper(swiperEl, {
      slidesPerView: 2,
      spaceBetween: 4,
      centeredSlides: true,
      loop: true,
      speed: 500,
      effect: 'slide',
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
      },
      navigation: {
        nextEl: nextDesktop,
        prevEl: prevDesktop,
      },
      breakpoints: {
        240: {
          slidesPerView: 1.1,
          navigation: {
            nextEl: nextMobile,
            prevEl: prevMobile,
          },
        },
        991: { slidesPerView: 2 },
      },
      on: {
        init: function () {
          if (ensureGSAPLoaded()) {
            var slides = swiperEl.querySelectorAll('.swiper-slide');
            var first = slides[0];
            var second = slides[1];
            var last = slides[slides.length - 1];
            if (first) window.gsap.set(first, { scale: 1, opacity: 1 });
            if (second) window.gsap.set(second, { scale: 0.95, opacity: 0.5 });
            if (last) window.gsap.set(last, { scale: 1, opacity: 0.5 });
          }
          updateSlideStyles(root);
        },
        transitionStart: function () {
          holdActiveSmall(root);
        },
        transitionEnd: function () {
          updateSlideStyles(root);
        },
      },
    });

    root.setAttribute(DATA_INITIALIZED, 'true');
    root._awSwiperInstance = swiper;
  }

  function destroyOne(root) {
    if (root._awSwiperInstance && root._awSwiperInstance.destroy) {
      root._awSwiperInstance.destroy(true, true);
      root._awSwiperInstance = null;
    }
    root.removeAttribute(DATA_INITIALIZED);
  }

  function initAll(options) {
    var opts = options || {};
    ensureSwiperLoaded(opts).then(function () {
      var roots = document.querySelectorAll(ROOT_SELECTOR);
      roots.forEach(function (root) {
        destroyOne(root);
        initOne(root, opts);
      });
    }).catch(function () {});
  }

  function run(options) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { initAll(options); });
    } else {
      initAll(options);
    }
  }

  /* Shopify theme editor: re-init when section is loaded, reordered, or selected */
  function setupShopifyEvents(options) {
    ['shopify:section:load', 'shopify:section:reorder', 'shopify:section:select'].forEach(function (eventName) {
      document.addEventListener(eventName, function (evt) {
        var sectionId = evt.detail && evt.detail.sectionId;
        var root = document.querySelector(ROOT_SELECTOR + '[data-aw-swiper-id="' + sectionId + '"]');
        if (root) {
          destroyOne(root);
          ensureSwiperLoaded(options).then(function () { initOne(root, options); }).catch(function () {});
        } else {
          initAll(options);
        }
      });
    });
  }

  /* Public API */
  window.AwSwiperComponent = {
    init: initAll,
    run: run,
    initOne: function (rootEl, opts) {
      if (rootEl && rootEl.classList && rootEl.classList.contains('aw-swiper')) {
        ensureSwiperLoaded(opts || {}).then(function () { initOne(rootEl, opts); }).catch(function () {});
      }
    },
    destroyOne: destroyOne,
    setupShopify: setupShopifyEvents,
  };

  /* Auto-run: Option A (use existing Swiper). For Option B pass { loadSwiperFromCDN: true } in theme. */
  run({ loadSwiperFromCDN: false });
  setupShopifyEvents({ loadSwiperFromCDN: false });
})();
